/**
 * How this console says what went wrong, written once.
 *
 * It started as two functions moved out of `Settings.tsx` when the brand section became its own
 * file (ADR 0081): both needed the same two sentences, and two copies of "this part is not deployed
 * yet" is how one of them stops being true. `describe` is now every screen's, for the same reason —
 * fourteen of them had written it out again, and the copies had already begun to disagree about
 * whether a machine code belongs in front of a customer.
 */
import { ApiError } from '../../api/client'

/**
 * What went wrong, in the API's own words and nothing else.
 *
 * ## The code used to be printed, and it read as a leak
 *
 * This returned `` `${message} (${code})` ``, and fourteen screens carried their own copy of the
 * same expression. The end-to-end run of 2026-09-15 reported the result as machine text in front of
 * a customer: *"Recipients could not load — no such envelope (not_found)"*, *"not found
 * (not_found)"*, *"(documents_not_ready)"*, *"(envelope_closed)"*. Nothing sensitive leaks — the
 * codes are in the published OpenAPI — but a sender reading `(envelope_closed)` learns nothing they
 * did not already learn from the sentence, and a parenthesised identifier is how software looks
 * when it is broken.
 *
 * The codes were kept for a reason that did not survive being checked: two screens still say the
 * code "tells an operator it is their role" beside `PT403`, whose message is already the whole
 * sentence — *"you do not have permission to create envelopes"*. Where a code really does carry
 * something the message does not, the answer is a sentence for that code (`describePhotoError`
 * below, `sendRemedy` in `Recipients.tsx`), not the code itself.
 *
 * ## One copy, and it takes any of the three error classes
 *
 * `ApiError`, `WriteError` and `ReadError` are all `Error`s with a message, so the instance test is
 * on `Error` and every screen can use this one. The fourteen copies are gone; a fifteenth cannot
 * disagree with it.
 */
export const describe = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong.'

/**
 * A 404 from a whole route means the DEPLOYMENT does not have it, not that a thing is missing.
 *
 * The API answers 404 for a path it does not serve, and it answers 404 for a row in another
 * tenant — deliberately, because "forbidden" on a cross-tenant id confirms the id exists. Those are
 * the same status and they are not the same problem, and `describe()` rendered both as
 * `not found (not_found)` — which is what the founder saw on production: a red line under
 * Appearance and under Single sign-on that named no cause and offered no remedy. The code is gone
 * from that sentence now; the two words that are left still name no cause, which is why this
 * function and not that one is what a 404 goes through.
 *
 * This app and its Worker do not deploy atomically (S27; CLAUDE.md §7), so a screen reaching a
 * route its Worker predates is an ORDINARY state of things between a merge and a promotion, not an
 * edge case — ADR 0109 and ADR 0113 narrow that window but neither closes it. Saying so is the
 * difference between "something is broken" and "this part is not deployed yet".
 *
 * ## It reads the CODE, not the status — which is what makes it safe on a write path
 *
 * It used to key on `status === 404` alone, and that forced it to stay narrow: a 404 on
 * `…/holds/{id}/release` can honestly mean the hold is gone, so the helper could only be trusted
 * where no id was in the path. The API now answers a path it does not serve with its own code —
 * `no_such_route` (`apps/api/src/index.ts`, the two dispatch misses) — while `notFound()` keeps
 * `not_found` for a row that is really absent.
 *
 * So the guess is gone. `no_such_route` is ALWAYS the deployment and never the data, which is why
 * this is now correct on a POST and on an id-bearing path, where guessing from the status would
 * have told somebody their hold had vanished. The `status === 404` arm stays as a fallback for
 * exactly as long as a Worker older than that change is still serving — which, on the day this was
 * written, was every Worker in production.
 *
 * ## `fallback`, and the backwards ternary it exists to retire
 *
 * Nine screens wrote their load handler as
 *
 *     setFailed(error instanceof ApiError ? error.message : 'Could not load the retention policy.')
 *
 * and every one of them had the two arms the wrong way round. The prose on the right was written
 * for exactly the case that never reaches it: an unroutable path answers
 * `{"code":"not_found","message":"not found"}`, which IS an `ApiError`, so `error.message` wins and
 * the screen prints the two words `not found`. The sentence the author wrote is unreachable.
 *
 * That is what the founder photographed on 2026-09-13 — "not found" under Document retention and
 * again under Legal holds, on a console whose Worker predates both routes.
 *
 * So `fallback` is where that prose goes, and it is used where it was always meant to be: when the
 * failure is NOT an `ApiError` at all (a network fault, a parse error), where there is no API
 * sentence to show. An `ApiError` that is not a 404 still shows the API's own message, because a
 * 403 or a 409 says something this screen could not.
 */
export function describeUnlessNotDeployed(error: unknown, what: string, fallback?: string): string {
  if (isNotDeployed(error)) return notDeployedSentence(what)
  if (fallback !== undefined && !(error instanceof ApiError)) return fallback
  return describe(error)
}

/**
 * The question `describeUnlessNotDeployed` answers, as a boolean, for a screen that renders the
 * not-deployed case as a STATE rather than as a sentence — Connect shows it as the section's
 * empty state, and a favourite star puts itself back. Same predicate, so the two can never
 * disagree about what counts as "the deployment".
 */
export function isNotDeployed(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'no_such_route' || error.status === 404)
}

/** The one sentence, written once. `what` is the subject: "Connect", "Favourites". */
export function notDeployedSentence(what: string): string {
  return `${what} is not available on this deployment yet — the API needs updating to the current release. Nothing is wrong with your organization's data.`
}

/**
 * The same question on a path that NAMES A ROW, where the two 404s are different answers.
 *
 * `…/legal-holds/{id}/release` can 404 because the route is not deployed OR because that hold is
 * genuinely gone — a colleague released it a moment ago. `describeUnlessNotDeployed` would call
 * both a deployment problem, which is a lie half the time and the worse half: it would tell
 * somebody to wait for a release when what they should do is refresh.
 *
 * Since the API distinguishes them by code, so can this. `no_such_route` is the deployment;
 * a bare `not_found` on a path carrying an id is the row.
 *
 * `fallback` is the same arm `describeUnlessNotDeployed` carries and is there for the same reason:
 * prose for a failure that is NOT an `ApiError` at all, where there is no API sentence to show.
 * The two helpers take their arguments in the same order and mean the same thing by them, so a
 * screen moving between them cannot get the arms the wrong way round — which is the whole defect
 * the section above is about.
 */
export function describeRowOrRoute(
  error: unknown,
  what: string,
  gone: string,
  fallback?: string,
): string {
  if (error instanceof ApiError && error.code === 'no_such_route') return notDeployedSentence(what)
  if (error instanceof ApiError && error.status === 404) return gone
  if (fallback !== undefined && !(error instanceof ApiError)) return fallback
  return describe(error)
}

/**
 * Would pressing Try again produce anything different? For a 404, no — and the button is a lie.
 *
 * Both 404s are settled facts from the browser's side. `no_such_route` stays a 404 until somebody
 * promotes a Worker, and a `not_found` on a path carrying an id stays a 404 until the row comes
 * back or the reader signs in somewhere else. Neither changes because the same request was sent a
 * second time, so a retry affordance on one is the dead end `ux.md` §12 bans: it reads as a remedy
 * and does nothing, which is worse than offering no button at all.
 *
 * Deliberately NOT every failure a retry cannot fix. A 403 can clear while the reader watches — an
 * admin grants the permission — and a 5xx or a dropped connection is the case retrying is FOR.
 * This is the narrow predicate for the status where a second attempt is knowably pointless, and
 * screens pair it with `ErrorState`'s `action` to send the reader somewhere that works.
 *
 * It is the status and not the code, so it covers both 404s. Which SENTENCE they get is
 * `describeRowOrRoute`'s question and it does read the code; this one is only about the button.
 */
export function retryingCannotHelp(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

/**
 * The largest profile photo the API will store — `MAX_PHOTO_BYTES` in
 * `apps/api/src/routes/profile-photo.ts`, stated here so the console can refuse an oversized file
 * before spending the upload.
 *
 * Duplicated rather than imported: `apps/web` does not depend on `apps/api`, and the alternative —
 * a shared constant in `packages/core` for one number — is more coupling than the number is worth.
 * If it moves, `photo-upload.test.tsx` pins this side of it.
 */
export const MAX_PHOTO_BYTES = 256 * 1024

/** What the API will store, and what the file input offers. Sniffed server-side; a header is a
 *  claim by the uploader, so this list is a courtesy to the person choosing, not a control. */
export const PHOTO_TYPES = ['image/png', 'image/jpeg'] as const

/**
 * Refuse a picture whose declared FORMAT the API will not store, in the words of the thing they
 * chose. Returns null when there is nothing to say.
 *
 * Applied before the browser is asked to resize anything, and never skipped, because this is the
 * refusal with a security consequence behind it. An SVG is a document rather than a picture and can
 * carry script; rasterising one to a JPEG would be a way of laundering it past a check whose whole
 * purpose is that it never gets stored (`packages/core/src/profile/photo.ts`).
 *
 * Not validation — the server sniffs the magic bytes and is the authority. `type` is what the
 * browser guessed from the extension and can be empty, and an empty one is let through: the server
 * sniffs, and refusing a file the API would have accepted is the worse error.
 */
export function photoFormatObjection(file: File): string | null {
  if (file.type !== '' && !(PHOTO_TYPES as readonly string[]).includes(file.type)) {
    return `That file is ${file.type}, and a profile photo has to be a PNG or a JPEG. SVG is refused because it can carry script.`
  }
  return null
}

/**
 * Refuse bytes the API is certain to refuse for their SIZE, and say by how much.
 *
 * Measured on what is about to be SENT rather than on what was chosen, which is the whole of the
 * change: the console now re-renders a picked photo to a 256 px square before it uploads
 * (`renderToPhotoSquare`), so a 4 MB phone photo is resized rather than turned away. What survives
 * this is a file no browser here could render — and then the number in the sentence is the one the
 * server would have answered with anyway, without spending the upload to hear it.
 */
export function photoSizeObjection(blob: Blob): string | null {
  if (blob.size <= MAX_PHOTO_BYTES) return null
  const kb = Math.ceil(blob.size / 1024)
  return `That picture is ${kb} KB and the limit is ${MAX_PHOTO_BYTES / 1024} KB. This browser could not resize it, so crop it or export it smaller — ${PHOTO_ADVICE}.`
}

/**
 * The shape and size to aim for, in one clause both the refusal above and the card's own sentence
 * read, so the advice cannot be given two ways.
 */
export const PHOTO_ADVICE = 'a square around 256 × 256 pixels is ideal'

/**
 * What went wrong with a profile photo, said so the reader knows what to do next.
 *
 * `describe()` rendered every one of these as `message (code)`, which is what the founder
 * photographed on production: "not found (not_found)" under a photo that would not upload. The 404
 * there was the DEPLOYMENT — the running Worker predated `/v1/profile/photo` (CLAUDE.md §7) — and
 * that is not something a reader can be expected to infer from either half.
 *
 * The 404 is the one that needs the code and not just the status: `POST /v1/profile/photo/microsoft`
 * answers 404 `no_photo` for the ordinary case of an account that simply has no picture, which is
 * not a deployment problem and must not be described as one.
 */
export function describePhotoError(error: unknown, what: 'upload' | 'import' | 'remove'): string {
  if (!(error instanceof ApiError)) return describe(error)
  switch (error.code) {
    case 'photo_too_large':
      return `That picture is over ${MAX_PHOTO_BYTES / 1024} KB. Crop it, or export it at a smaller size, and try again.`
    case 'unsupported_image':
      return 'A profile photo has to be a PNG or a JPEG. SVG is refused because it can carry script, and other formats are not stored.'
    case 'photo_too_small':
      return 'That file is too small to be an image. Pick the picture itself rather than a shortcut or a placeholder.'
    case 'no_photo':
      return 'That Microsoft 365 account has no profile photo set. Add one in Microsoft 365, or upload a picture here instead.'
    case 'not_configured':
      return 'This deployment cannot open a Microsoft 365 connection. Upload a picture instead.'
    default:
      break
  }
  if (error.status === 404) {
    const verb =
      what === 'upload'
        ? 'Uploading a photo'
        : what === 'import'
          ? 'Importing a photo'
          : 'Removing your photo'
    return `${verb} is not available on this deployment yet — the API needs updating to the current release. Your account is fine; nothing was changed.`
  }
  if (error.status === 401 || error.status === 403) {
    return 'Your session is not allowed to change this photo. Sign out and back in, and try again.'
  }
  if (error.status === 413) {
    return `That picture is over ${MAX_PHOTO_BYTES / 1024} KB. Crop it, or export it at a smaller size, and try again.`
  }
  return describe(error)
}
